import { useEffect, useRef } from 'react';
import jsQR from 'jsqr';

interface Props {
  onScan: (value: string) => void;
  onError: (message: string) => void;
}

export default function RetailIdQrScanner({ onScan, onError }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    let disposed = false;
    let stream: MediaStream | undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d', { willReadFrequently: true });

    const stop = () => {
      if (timer !== undefined) clearTimeout(timer);
      stream?.getTracks().forEach((track) => track.stop());
      if (video) video.srcObject = null;
    };

    const scan = () => {
      if (disposed || !video || !context) return;
      try {
        if (video.readyState >= 2 && video.videoWidth > 0 && video.videoHeight > 0) {
          const scale = Math.min(1, 960 / video.videoWidth);
          canvas.width = Math.round(video.videoWidth * scale);
          canvas.height = Math.round(video.videoHeight * scale);
          context.drawImage(video, 0, 0, canvas.width, canvas.height);
          const image = context.getImageData(0, 0, canvas.width, canvas.height);
          const code = jsQR(image.data, image.width, image.height);
          if (code?.data) {
            stop();
            onScan(code.data);
            return;
          }
        }
        timer = setTimeout(scan, 150);
      } catch {
        stop();
        onError('Could not read the camera image. Paste a QR URL or Retail ID instead.');
      }
    };

    async function start() {
      try {
        if (!navigator.mediaDevices?.getUserMedia || !context || !video) {
          onError('Camera scanning is unavailable. Use HTTPS and allow camera access, or paste a QR URL or Retail ID.');
          return;
        }
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false,
        });
        if (disposed) {
          stop();
          return;
        }
        video.srcObject = stream;
        await video.play();
        if (!disposed) scan();
      } catch {
        stop();
        if (!disposed) onError('Could not start the camera. Allow camera access or paste a QR URL or Retail ID instead.');
      }
    }

    void start();
    return () => {
      disposed = true;
      stop();
    };
  }, [onScan, onError]);

  return (
    <div className="stack">
      <p className="muted" role="status">Point your camera at a Retail ID QR code.</p>
      <video ref={videoRef} muted playsInline aria-label="Retail ID QR camera" style={{ width: '100%', maxWidth: 640 }} />
    </div>
  );
}
